import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Title } from '../../components/Title';
import {
  getApplicationBreakpointState,
  getApplicationModeState,
  getMatchDetailStatus,
  getMatches,
  getMatchesStatus,
  getMatchesTotalCount,
  getUser,
} from '../../selectors';
import { MatchTable, MatchTableType } from '../../components/MatchTable';
import { ApplicationBreakpoint, ApplicationMode, Match, Status } from '../../types';
import { MatchCard, MatchCardType } from '../../components/MatchCard';
import { setMatches } from '../../actions';
import { ContentLoader } from '../../components/ContentLoader';
import { FloatingAddButton } from '../../components/FloatingAddButton';
import { MobileTitle } from '../../components/MobileTItle';

import './MatchList.scss';
import { setMatchDetailTabToLocalStorage } from '../../utils';
import { NoMatches } from '../../components/NoMatches';

export const MatchListPage: FunctionComponent<{}> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    setMatchDetailTabToLocalStorage({ id: 0, name: '' });
  }, []);

  // const [currentScrolled, setCurrentScrolled] = useState<number>(0);
  // const [maxScrolled, setMaxScrolled] = useState<number>(0);

  const applicationBreakpoint = useSelector(getApplicationBreakpointState);
  const applicationMode = useSelector(getApplicationModeState);
  const matches = useSelector(getMatches);
  const totalCount = useSelector(getMatchesTotalCount);
  const matchesStatus = useSelector(getMatchesStatus);
  const user = useSelector(getUser);
  const matchDetailStatus = useSelector(getMatchDetailStatus);

  const matchListClasses = ['MatchList'];
  if (applicationMode === ApplicationMode.dark) matchListClasses.push('MatchList--dark');

  // useEffect(() => {
  //   const handleScroll = (event: any) => {
  //     setCurrentScrolled(event.target.scrollTop);
  //     setMaxScrolled(event.target.scrollTopMax);
  //
  //     const percentage = (currentScrolled / maxScrolled) * 100;
  //     console.log(percentage);
  //   };
  //
  //   matchListRef.current.addEventListener('scroll', handleScroll);
  //
  //   return () => matchListRef.current.removeEventListener('scroll', handleScroll);
  // });

  const loadMore = useCallback(() => {
    if (user && matches.length < totalCount && matchesStatus !== Status.requestingMore) {
      const start = matches.length;
      dispatch(setMatches('', start, 'DESC'));
    }
  }, [user, matches.length, dispatch, matchesStatus, totalCount]);

  const observer = useRef<any>();
  const lastMatchRef = useCallback(
    node => {
      if (observer.current) {
        observer.current.disconnect();
      }
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      });

      if (node) {
        observer.current.observe(node);
      }
    },
    [loadMore]
  );

  const redirectToNewMatch = () => {
    history.push(`/match-detail?isNew=true`);
  };

  const redirectToMatch = (match: Match) => {
    history.push(`/match-detail?id=${match.id}`);
  };

  useEffect(() => {
    if (user || matchDetailStatus === Status.success) {
      const start = 0;
      dispatch(setMatches('', start, 'DESC'));
    }
  }, [dispatch, user, t, matchDetailStatus]);

  const dividerIndex = useMemo(() => {
    const now = new Date();
    const idx = matches.findIndex((match: Match) => match.date <= now);
    return idx > 0 ? idx : undefined;
  }, [matches]);

  const renderMatchCards = matches.map((match: Match, index: number) => {
    const redirect = () => {
      redirectToMatch(match);
    };
    const card = (
      <MatchCard type={MatchCardType.all} key={index} match={match} redirectToMatch={redirect} />
    );
    return index === dividerIndex ? (
      <React.Fragment key={`divider-${index}`}>
        <div className="MatchList__divider" />
        {card}
      </React.Fragment>
    ) : (
      card
    );
  });

  const renderIsLoadingMore = () =>
    matchesStatus === Status.requestingMore && (
      <div style={{ paddingBlock: '1rem' }}>
        <ContentLoader />
      </div>
    );

  return (
    <div className={matchListClasses.join(' ')}>
      {matchesStatus !== Status.requesting ? (
        <React.Fragment>
          {applicationBreakpoint === ApplicationBreakpoint.desktop ? (
            <Title title={t('matches')} />
          ) : (
            <MobileTitle title={t('matches')} />
          )}
          <div className="MatchList__scrollable">
            <div className="MatchList__content">
              {applicationBreakpoint === ApplicationBreakpoint.desktop ? (
                <div style={{ minWidth: 'fit-content', width: '100%' }}>
                  <div>
                    {matches.length > 0 && (
                      <>
                        <MatchTable
                          type={MatchTableType.all}
                          matches={matches}
                          redirectToMatch={redirectToMatch}
                          dividerIndex={dividerIndex}
                        />
                        <div ref={lastMatchRef}>&nbsp;</div>
                        {renderIsLoadingMore()}
                      </>
                    )}
                    {matches.length === 0 && (
                      <NoMatches
                        type={MatchTableType.noData}
                        redirectToNewMatch={redirectToNewMatch}
                        withMatchtable
                      />
                    )}
                  </div>
                </div>
              ) : (
                <React.Fragment>
                  {matches.length > 0 && (
                    <>
                      {renderMatchCards}
                      <div ref={lastMatchRef}>&nbsp;</div>
                      {renderIsLoadingMore()}
                    </>
                  )}
                  {matches.length === 0 && (
                    <NoMatches
                      type={MatchTableType.noData}
                      redirectToNewMatch={redirectToNewMatch}
                    />
                  )}
                </React.Fragment>
              )}
            </div>
          </div>
          {matches.length > 0 && <FloatingAddButton handleClick={redirectToNewMatch} />}
        </React.Fragment>
      ) : (
        <ContentLoader />
      )}
    </div>
  );
};
